import BusinessVerificationPanel from './BusinessVerificationPanel'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { navigationTargets } from '@/services/vault/navigationTargets'
import { categoriesFor, categoryLabels } from '@/services/vault/sourceReviews'
import { LockKeyhole, Download, RefreshCw } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  clientPath,
  filePath,
  read,
  command,
  download,
  upload,
  scanJobPath,
  type VaultUpload,
  type VaultPublication,
  type PendingVaultUploads,
  type Page,
  type Profile,
  type ProfileVersion,
  type PortableProfile,
  type VaultClient,
  type VaultFile,
  type VaultService,
} from '@/services/vault/vault.http'
import { Field, ServiceSelect, useCommand, CommandStatus, RETENTION } from './VaultForms'
import RelationshipsPanel from './RelationshipsPanel'
import SourceReviewsPanel from './SourceReviewsPanel'
import HistoricalDocumentsPanel from './HistoricalDocumentsPanel'
import SubjectVerificationPanel from './SubjectVerificationPanel'

export default function VaultPage() {
  const { user } = useAuth()
  const authorityKey = JSON.stringify([
    user?.id,
    user?.currentSessionId,
    user?.authScope,
    user?.tenantId,
    user?.role,
    [...(user?.permissions ?? [])].sort(),
  ])
  return <VaultWorkspace key={authorityKey} authorityKey={authorityKey} />
}

function VaultWorkspace({ authorityKey }: { authorityKey: string }) {
  const { user } = useAuth(),
    { clientId } = useParams(),
    queryClient = useQueryClient()
  const [search] = useSearchParams()
  const independent = user?.authScope === 'client_vault'
  const [selected, setSelected] = useState(clientId ?? ''),
    [service, setService] = useState<VaultService>('income_tax'),
    [versionId, setVersionId] = useState('')
  const [tab, setTab] = useState<'profile' | 'files' | 'access' | 'reviews' | 'historical'>(() =>
    search.get('tab') === 'files' ? 'files' : 'profile'
  )
  const accountKey = ['vault', authorityKey]
  const clients = useQuery({
    queryKey: [...accountKey, 'clients'],
    queryFn: () =>
      read<{
        clients?: VaultClient[]
        controllers?: Array<VaultClient & { controller_role: string }>
      }>('/clients'),
    enabled: independent || ['firm_owner', 'firm_admin'].includes(user?.role ?? ''),
    staleTime: 0,
  })
  const assignedStaff =
    !independent &&
    ['ca_partner', 'technical_assistant', 'research_assistant'].includes(user?.role ?? '')
  const staffTargets = useInfiniteQuery({
    queryKey: [...accountKey, 'staff-navigation-targets'],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => navigationTargets(pageParam),
    getNextPageParam: last => last.next_after ?? undefined,
    enabled: assignedStaff,
    staleTime: 0,
    retry: false,
  })
  const liveStaffTargets =
    staffTargets.isSuccess && !staffTargets.isFetching ? staffTargets.data : undefined
  const liveClients = clients.isSuccess && !clients.isFetching ? clients.data : undefined
  const choices = useMemo<Array<VaultClient & { display_name?: string }>>(
    () =>
      assignedStaff
        ? (liveStaffTargets?.pages.flatMap(page => page.clients) ?? [])
        : (liveClients?.controllers ?? liveClients?.clients ?? []),
    [assignedStaff, liveStaffTargets, liveClients]
  )
  useEffect(() => {
    setSelected(clientId ?? '')
    setVersionId('')
  }, [clientId])
  useEffect(() => {
    if (!selected && choices.length === 1) setSelected(choices[0].client_id)
  }, [selected, choices])
  const controller = liveClients?.controllers?.find(row => row.client_id === selected)
  const owner = independent && controller?.controller_role === 'owner'
  const canUpload = owner || (!independent && !!user?.permissions?.includes('txh.vault.upload'))
  const canPublish =
    !independent &&
    ['firm_owner', 'firm_admin', 'ca_partner'].includes(user?.role ?? '') &&
    !!user?.permissions?.includes('txh.vault.publish')
  const reviewOwner =
    owner &&
    !clients.isFetching &&
    !clients.isError &&
    ['individual_client', 'business_owner_client'].includes(user?.role ?? '')
  const reviewFirm =
    !independent &&
    ['firm_owner', 'firm_admin', 'ca_partner'].includes(user?.role ?? '') &&
    !!user?.permissions?.includes('txh.document.read')
  const history = useQuery({
    queryKey: [...accountKey, selected, service, 'profile-versions'],
    queryFn: () =>
      read<Page<ProfileVersion>>(`${clientPath(selected)}/profile/versions`, {
        service,
        page_size: 100,
      }),
    enabled: !!selected && tab === 'profile',
    staleTime: 0,
  })
  const currentHistory = history.isSuccess && !history.isFetching
  const resolvedVersion =
    versionId || (currentHistory ? history.data?.items[0]?.version_id : undefined)
  const profile = useQuery({
    queryKey: [...accountKey, selected, service, 'profile', resolvedVersion],
    queryFn: () =>
      read<Profile>(`${clientPath(selected)}/profile`, { service, version_id: resolvedVersion }),
    enabled: !!selected && currentHistory && !!resolvedVersion && tab === 'profile',
    staleTime: 0,
  })
  const [filePage, setFilePage] = useState(1)
  const files = useQuery({
    queryKey: [...accountKey, selected, 'files', filePage],
    queryFn: () => read<Page<VaultFile>>(`${filePath(selected)}/versions`, { page: filePage }),
    enabled: !!selected && tab === 'files',
    staleTime: 0,
  })
  const liveHistory = history.isSuccess && !history.isFetching ? history.data : undefined
  const liveProfile =
    liveHistory && profile.isSuccess && !profile.isFetching ? profile.data : undefined
  const liveFiles = files.isSuccess && !files.isFetching ? files.data : undefined
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: accountKey })
  }
  const actions = useCommand()
  return (
    <div className="space-y-6 text-text-primary">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex gap-2 items-center">
            <LockKeyhole className="w-6 h-6" />
            {independent ? 'My Client Vault' : 'Client Vaults'}
          </h1>
          <p className="text-sm text-text-secondary">
            Client-owned profiles and immutable file versions, with explicit firm access.
          </p>
        </div>
        <button className="btn btn-secondary" onClick={() => void refresh()}>
          <RefreshCw className="w-4 h-4" />
          Refresh authority
        </button>
      </div>
      <SubjectVerificationPanel />
      <BusinessVerificationPanel />
      <div className="card card-elevated p-4 space-y-3">
        <label className="block text-sm text-text-secondary">
          Client
          <select
            aria-label="Client"
            className="input input-bordered w-full mt-1"
            value={selected}
            onChange={event => {
              setSelected(event.target.value)
              setVersionId('')
              setFilePage(1)
            }}
          >
            <option value="">Select a client</option>
            {choices.map(row => (
              <option key={row.client_id} value={row.client_id}>
                {row.display_name
                  ? `${row.display_name} · ${row.client_id.slice(0, 8)}`
                  : row.client_id}{' '}
                {row.status ? `(${row.status})` : ''}
              </option>
            ))}
            {selected && !choices.some(row => row.client_id === selected) && (
              <option value={selected}>{selected}</option>
            )}
          </select>
        </label>
        {!independent && (
          <p className="text-xs text-text-secondary">
            Firm administrators see relationships here. Assigned staff see current authorized
            workspace targets. Each Vault operation checks its own client grant.
          </p>
        )}
        {independent && (
          <Link
            className="text-primary-600 dark:text-primary-400 text-sm"
            to="/client-vault/account"
          >
            Account sessions
          </Link>
        )}
        {clients.error && <p role="alert">{clients.error.message}</p>}
        {assignedStaff && staffTargets.isFetching && (
          <p role="status">Checking assigned client choices…</p>
        )}
        {assignedStaff && staffTargets.error && <p role="alert">{staffTargets.error.message}</p>}
        {assignedStaff && staffTargets.hasNextPage && (
          <button
            className="btn btn-secondary"
            disabled={staffTargets.isFetching}
            onClick={() => void staffTargets.fetchNextPage()}
          >
            Load more assigned clients
          </button>
        )}
      </div>
      {selected && (
        <>
          <nav className="flex flex-wrap gap-2" aria-label="Vault sections">
            {(['profile', 'files', 'access', 'reviews', 'historical'] as const)
              .filter(value => value !== 'access' || owner || !independent)
              .filter(value => value !== 'reviews' || reviewOwner || reviewFirm)
              .filter(value => value !== 'historical' || reviewOwner)
              .map(value => (
                <button
                  key={value}
                  className="btn btn-secondary capitalize"
                  aria-pressed={tab === value}
                  onClick={() => setTab(value)}
                >
                  {value === 'access'
                    ? 'Firm access & switching'
                    : value === 'historical'
                      ? 'Historical documents'
                      : value === 'reviews'
                        ? 'Document reviews'
                        : value}
                </button>
              ))}
          </nav>
          {tab === 'profile' && (
            <section className="card card-elevated p-5 space-y-4">
              <ServiceSelect
                value={service}
                onChange={value => {
                  setService(value)
                  setVersionId('')
                }}
              />
              {history.isPending && <p role="status">Loading authorized profile history…</p>}
              {history.error && <p role="alert">{history.error.message}</p>}
              {liveHistory && liveHistory.items.length > 0 && (
                <label className="block text-sm text-text-secondary">
                  Authorized version
                  <select
                    className="input input-bordered w-full"
                    value={resolvedVersion}
                    onChange={event => setVersionId(event.target.value)}
                  >
                    {liveHistory.items.map(row => (
                      <option key={row.version_id} value={row.version_id}>
                        Version {row.version_number}
                        {row.basis === 'retention' ? ' — retained history' : ''}
                      </option>
                    ))}
                  </select>
                  {liveHistory.total > liveHistory.items.length && (
                    <span>Showing the latest 100 authorized versions.</span>
                  )}
                </label>
              )}
              {profile.error && <p role="alert">{profile.error.message}</p>}
              {profile.isFetching && <p role="status">Checking live access…</p>}
              {liveProfile && liveProfile.read_only && (
                <p className="text-sm text-text-secondary">
                  Read-only access.{' '}
                  {liveProfile.basis === 'retention'
                    ? RETENTION
                    : 'Your current authority does not include profile updates.'}
                </p>
              )}
              {(liveProfile || liveHistory?.total === 0) && (
                <ProfileEditor
                  key={`${selected}-${resolvedVersion ?? 'new'}`}
                  client={selected}
                  service={service}
                  current={liveProfile}
                  canCreate={owner}
                  onSaved={refresh}
                />
              )}
            </section>
          )}
          {tab === 'files' && (
            <section className="space-y-4">
              <div className="card card-elevated p-5 space-y-4">
                <h2 className="font-semibold">Authorized file versions</h2>
                {files.isFetching && <p role="status">Checking live access…</p>}
                {files.error && <p role="alert">{files.error.message}</p>}
                {liveFiles?.items.length === 0 && (
                  <p className="text-text-secondary">No authorized published files.</p>
                )}
                {liveFiles?.items.map(file => (
                  <article
                    key={file.version_id}
                    className="border border-surface-overlay rounded-lg p-4 space-y-2"
                  >
                    <div className="flex flex-wrap justify-between gap-2">
                      <h3 className="font-medium break-all">
                        {file.filename} · version {file.version_number}
                      </h3>
                      <button
                        className="btn btn-secondary"
                        disabled={actions.busy}
                        onClick={() => void actions.run(() => download(selected, file))}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </div>
                    <p className="text-sm text-text-secondary">
                      {file.classification === 'client_output'
                        ? 'Professional output'
                        : 'Client document'}{' '}
                      · {file.service} · {file.category} · {file.financial_year ?? 'Undated'} ·{' '}
                      {file.byte_size} bytes
                    </p>
                    {file.origin_firm_id && (
                      <p className="text-xs text-text-tertiary break-all">
                        Origin firm: {file.origin_firm_id}
                      </p>
                    )}
                    <p className="text-xs text-text-tertiary break-all">
                      SHA-256: {file.content_sha256}
                    </p>
                    {file.basis === 'retention' && (
                      <p className="text-sm text-text-secondary">
                        Retained read-only version. {RETENTION}
                      </p>
                    )}
                  </article>
                ))}
                {liveFiles && (
                  <div className="flex items-center gap-3">
                    <button
                      className="btn btn-secondary"
                      disabled={filePage === 1}
                      onClick={() => setFilePage(page => page - 1)}
                    >
                      Previous
                    </button>
                    <span>
                      Page {filePage} · {liveFiles.total} authorized versions
                    </span>
                    <button
                      className="btn btn-secondary"
                      disabled={filePage * liveFiles.page_size >= liveFiles.total}
                      onClick={() => setFilePage(page => page + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
                <CommandStatus {...actions} />
              </div>
              {(canUpload || canPublish) && (
                <UploadPanel
                  key={JSON.stringify([...accountKey, selected])}
                  client={selected}
                  firm={!independent}
                  canUpload={canUpload}
                  canPublish={canPublish}
                  accountKey={accountKey}
                  onSaved={refresh}
                />
              )}
            </section>
          )}
          {tab === 'access' && (
            <RelationshipsPanel
              key={selected}
              client={selected}
              owner={owner}
              accountKey={accountKey}
              onSaved={refresh}
            />
          )}
          {tab === 'historical' && reviewOwner && (
            <HistoricalDocumentsPanel
              client={selected}
              owner={reviewOwner}
              accountKey={accountKey}
            />
          )}
          {tab === 'reviews' && (reviewOwner || reviewFirm) && (
            <SourceReviewsPanel
              key={JSON.stringify([...accountKey, selected, user?.role, user?.permissions])}
              client={selected}
              owner={reviewOwner}
              accountKey={accountKey}
            />
          )}
        </>
      )}
    </div>
  )
}

function ProfileEditor({
  client,
  service,
  current,
  canCreate,
  onSaved,
}: {
  client: string
  service: VaultService
  current?: Profile
  canCreate: boolean
  onSaved: () => Promise<void>
}) {
  const [value, setValue] = useState<PortableProfile>(current?.profile ?? { display_name: '' }),
    actions = useCommand()
  const editable = current
    ? !current.read_only && current.allowed_actions.includes('profile.update')
    : canCreate
  const fields: Array<[keyof PortableProfile, string]> = [
    ['display_name', 'Display name'],
    ['contact_email', 'Contact email'],
    ['contact_phone', 'Contact phone'],
    ['postal_address', 'Postal address'],
    ['pan', 'PAN'],
    ['date_of_birth', 'Date of birth'],
    ['business_registration_number', 'Business registration number'],
  ]
  return (
    <form
      className="space-y-3"
      onSubmit={event => {
        event.preventDefault()
        const payload = {
          profile: Object.fromEntries(
            Object.entries(value).map(([key, text]) => [key, text || null])
          ),
          service,
          expected_version_id: current?.version_id ?? null,
        }
        void actions.run(async () => {
          await command(
            `${clientPath(client)}/profile`,
            { ...payload, idempotency_key: actions.idempotencyKey(payload) },
            'put'
          )
          await onSaved()
        })
      }}
    >
      {fields.map(([field, label]) => (
        <Field
          key={field}
          label={label}
          type={field === 'date_of_birth' ? 'date' : field === 'contact_email' ? 'email' : 'text'}
          required={field === 'display_name'}
          disabled={!editable || actions.busy}
          value={value[field] ?? ''}
          onChange={event => setValue({ ...value, [field]: event.target.value })}
        />
      ))}
      {editable && (
        <button className="btn btn-primary" disabled={actions.busy}>
          Save new profile version
        </button>
      )}
      <CommandStatus {...actions} />
    </form>
  )
}
function UploadPanel({
  client,
  firm,
  canUpload,
  canPublish,
  accountKey,
  onSaved,
}: {
  client: string
  firm: boolean
  canUpload: boolean
  canPublish: boolean
  accountKey: unknown[]
  onSaved: () => Promise<void>
}) {
  const [file, setFile] = useState<File | null>(null),
    [service, setService] = useState<VaultService>('income_tax'),
    [category, setCategory] = useState(''),
    [year, setYear] = useState(''),
    [classification, setClassification] = useState<'client_controlled' | 'client_output'>(
      canUpload ? 'client_controlled' : 'client_output'
    ),
    [legacySource, setLegacySource] = useState(''),
    [portabilityApproved, setPortabilityApproved] = useState(false),
    [staged, setStaged] = useState<VaultUpload | null>(null),
    [published, setPublished] = useState(false),
    actions = useCommand()
  const [recover, setRecover] = useState(false),
    [cursor, setCursor] = useState<string | null>(null)
  useEffect(() => {
    const replacement =
      classification === 'client_output' && !canPublish && canUpload
        ? 'client_controlled'
        : classification === 'client_controlled' && !canUpload && canPublish
          ? 'client_output'
          : null
    if (replacement) {
      setFile(null)
      setCategory('')
      setYear('')
      setLegacySource('')
      setPortabilityApproved(false)
      setClassification(replacement)
    }
  }, [classification, canUpload, canPublish])
  const pending = useQuery({
    queryKey: [...accountKey, client, 'pending-uploads', cursor],
    queryFn: () =>
      read<PendingVaultUploads>(
        `${filePath(client)}/scan-jobs`,
        cursor ? { after: cursor } : undefined
      ),
    enabled: recover,
    staleTime: 0,
    retry: false,
  })
  const confirmPublication = async (jobId: string, versionId: string) => {
    const result = await command<VaultPublication>(scanJobPath(jobId))
    if (
      result.state !== 'published' ||
      result.job_id !== jobId ||
      result.client_id !== client ||
      result.version_id !== versionId
    )
      throw new Error('Publication is not confirmed for this upload. Keep the upload reference.')
  }
  return (
    <section className="card card-elevated p-5 space-y-4">
      <h2 className="font-semibold">Upload a client document</h2>
      <p className="text-sm text-text-secondary">
        A new immutable version is held for malware scanning. Publish only after a trusted clean
        result. Authorization is checked again when publishing.
      </p>
      <form
        className="space-y-3"
        onSubmit={event => {
          event.preventDefault()
          if (classification === 'client_output' ? !canPublish : !canUpload) return
          if ((!file && !legacySource) || (legacySource && !portabilityApproved)) return
          const payload = {
            classification,
            legacySource,
            portabilityApproved: !!legacySource && portabilityApproved,
            service,
            category,
            financial_year: year || null,
            filename: file?.name,
            size: file?.size,
            modified: file?.lastModified,
          }
          void actions.run(async () => {
            const metadata = {
              classification,
              service,
              category,
              financial_year: year || null,
              idempotency_key: actions.idempotencyKey(payload),
            }
            setPublished(false)
            setStaged(
              legacySource
                ? await command<VaultFile>(`${filePath(client)}/legacy-publications`, {
                    source_document_id: legacySource,
                    approve_portability: portabilityApproved,
                    metadata,
                  })
                : await upload(client, file!, metadata)
            )
          })
        }}
      >
        {firm && (
          <>
            <label className="block text-sm text-text-secondary">
              Publication type
              <select
                className="input input-bordered w-full"
                value={classification}
                onChange={event => {
                  setFile(null)
                  setCategory('')
                  setYear('')
                  setLegacySource('')
                  setPortabilityApproved(false)
                  setClassification(event.target.value as typeof classification)
                }}
              >
                {canUpload && <option value="client_controlled">Client document</option>}
                {canPublish && <option value="client_output">Approved professional output</option>}
              </select>
            </label>
            <Field
              label="Existing firm document UUID (optional; source must already be eligible for client sharing)"
              value={legacySource}
              onChange={event => {
                setLegacySource(event.target.value)
                setPortabilityApproved(false)
              }}
            />
            {legacySource && (
              <label className="flex items-start gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={portabilityApproved}
                  onChange={event => setPortabilityApproved(event.target.checked)}
                />
                I approve copying this document into the client’s vault for authorized sharing.
              </label>
            )}
            <p className="text-xs text-text-secondary">
              Private notes, drafts and confidential documents cannot be published. Approved outputs
              keep their origin firm and immutable bytes.
            </p>
          </>
        )}
        <ServiceSelect value={service} onChange={setService} />
        <label className="block text-sm text-text-secondary">
          Portable category
          <select
            className="input input-bordered w-full"
            value={category}
            required
            onChange={event => setCategory(event.target.value)}
          >
            <option value="">Select a category</option>
            {categoriesFor(classification).map(value => (
              <option key={value} value={value}>
                {categoryLabels[value]}
              </option>
            ))}
          </select>
        </label>
        <Field
          label="Financial year (YYYY-YY); leave empty for undated"
          value={year}
          pattern="20[0-9]{2}-[0-9]{2}"
          onChange={event => setYear(event.target.value)}
        />
        <Field
          label="File (maximum 50 MB)"
          key={classification}
          type="file"
          required={!legacySource}
          disabled={!!legacySource}
          onChange={event => setFile(event.target.files?.[0] ?? null)}
        />
        <button
          className="btn btn-primary"
          disabled={
            actions.busy ||
            (classification === 'client_output' ? !canPublish : !canUpload) ||
            (!!staged && 'scan_job_id' in staged && !published) ||
            (!file && !legacySource) ||
            (!!legacySource && !portabilityApproved) ||
            (!!file && !legacySource && file.size > 50 * 1024 * 1024)
          }
        >
          Upload for scanning
        </button>
      </form>
      {staged && 'scan_job_id' in staged && (
        <div className="space-y-2">
          <p role="status">
            {published
              ? 'Document published.'
              : 'Upload received. Scan and publication are not yet confirmed.'}
          </p>
          <p className="text-sm text-text-secondary break-all">
            Upload reference: {staged.scan_job_id}
          </p>
          {!published && (
            <>
              <p className="text-sm text-text-secondary">
                Continue the same upload to scan and publish it. If the result is unavailable, retry
                here using the same upload reference.
              </p>
              <button
                className="btn btn-secondary"
                disabled={actions.busy}
                onClick={() =>
                  void actions.run(async () => {
                    if (staged.client_id !== client)
                      throw new Error('Upload belongs to a different client.')
                    await confirmPublication(staged.scan_job_id, staged.version_id)
                    setPublished(true)
                    await onSaved()
                  })
                }
              >
                Scan and publish upload
              </button>
            </>
          )}
        </div>
      )}
      {staged && !('scan_job_id' in staged) && (
        <div className="space-y-2">
          <p role="status">
            {staged.filename}: {staged.state}
          </p>
          <button
            className="btn btn-secondary"
            disabled={actions.busy}
            onClick={() =>
              void actions.run(async () => {
                setStaged(await read(`${filePath(client)}/versions/${staged.version_id}`))
              })
            }
          >
            Check scan status
          </button>
          {staged.state === 'clean' && (
            <button
              className="btn btn-secondary"
              disabled={actions.busy}
              onClick={() =>
                void actions.run(async () => {
                  setStaged(
                    await command(`${filePath(client)}/versions/${staged.version_id}:publish`)
                  )
                  await onSaved()
                })
              }
            >
              Publish clean version
            </button>
          )}
        </div>
      )}
      <div className="space-y-2">
        <button
          className="btn btn-secondary"
          disabled={actions.busy || pending.isFetching}
          onClick={() => {
            if (recover) void pending.refetch()
            else setRecover(true)
          }}
        >
          Recover pending uploads
        </button>
        {recover && (
          <>
            <p className="text-sm text-text-secondary">
              Only uploads from this account’s current session appear here. Continue an existing
              reference rather than uploading the same file again. An unresolved result does not
              mean publication failed.
            </p>
            {pending.isFetching && <p role="status">Checking pending uploads…</p>}
            {pending.error && (
              <p role="alert">Pending uploads unavailable. {pending.error.message}</p>
            )}
            {!pending.isFetching && !pending.error && pending.data?.items.length === 0 && (
              <p>No pending uploads on this page for this session.</p>
            )}
            {pending.data?.items
              .filter(
                row => !(staged && 'scan_job_id' in staged && staged.scan_job_id === row.job_id)
              )
              .map(row => (
                <div key={row.job_id} className="space-y-2">
                  <p className="text-sm break-all">Pending upload: {row.job_id}</p>
                  <button
                    className="btn btn-secondary max-w-full whitespace-normal break-all"
                    aria-label={`Continue upload ${row.job_id}`}
                    disabled={actions.busy || pending.isFetching || pending.isError}
                    onClick={() =>
                      void actions.run(async () => {
                        if (row.client_id !== client)
                          throw new Error('Upload belongs to a different client.')
                        await confirmPublication(row.job_id, row.version_id)
                        await onSaved()
                      })
                    }
                  >
                    Continue upload
                  </button>
                </div>
              ))}
            {cursor && (
              <button
                className="btn btn-secondary"
                disabled={pending.isFetching || actions.busy}
                onClick={() => setCursor(null)}
              >
                First pending page
              </button>
            )}
            {pending.data?.next_cursor && (
              <button
                className="btn btn-secondary"
                disabled={pending.isFetching || actions.busy}
                onClick={() => setCursor(pending.data!.next_cursor)}
              >
                Next pending page
              </button>
            )}
          </>
        )}
      </div>
      <CommandStatus {...actions} />
    </section>
  )
}
